import { useEffect, useState } from "react";
import { BASE_URL } from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { isNativeApp } from "@/utils/platform";

/**
 * Pont push natif (Capacitor) -> API.
 * Le site est chargé via server.url dans le shell Capacitor : le App.tsx du shell
 * n'est donc PAS exécuté. Le pont Capacitor est injecté dans cette page, on
 * s'enregistre donc directement ici auprès de PushNotifications.
 * On garde aussi les fallbacks (window.getNativeToken, event, localStorage).
 */
const RegisterDevice = () => {
  const { token, user } = useAuth();
  const [pendingToken, setPendingToken] = useState<string | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const accept = (t?: string | null) => {
      if (t) {
        localStorage.setItem("push_token", t);
        setPendingToken(t);
      }
    };

    // Fallbacks
    const getNativeToken = (window as any).getNativeToken;
    if (typeof getNativeToken === "function") accept(getNativeToken());
    else accept(localStorage.getItem("push_token"));

    const handleTokenReceived = (event: Event) => {
      accept((event as CustomEvent<string>).detail);
    };
    window.addEventListener("onTokenReceived", handleTokenReceived);

    const handles: Array<{ remove: () => Promise<void> }> = [];
    let cancelled = false;

    const setupNative = async () => {
      if (!isNativeApp()) return;
      try {
        const { PushNotifications } = await import("@capacitor/push-notifications");

        handles.push(
          await PushNotifications.addListener("registration", (t) => {
            console.log("✅ Push token natif :", t.value);
            (window as any).getNativeToken = () => t.value;
            accept(t.value);
          })
        );
        handles.push(
          await PushNotifications.addListener("registrationError", (err) => {
            console.error("❌ Push registration error:", err);
          })
        );
        if (cancelled) return;

        let perm = await PushNotifications.checkPermissions();
        if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
          perm = await PushNotifications.requestPermissions();
        }
        if (perm.receive !== "granted") {
          console.log("Notifications refusées");
          return;
        }
        await PushNotifications.register();
      } catch (e) {
        console.error("❌ PushNotifications indisponible :", e);
      }
    };

    setupNative();

    return () => {
      cancelled = true;
      window.removeEventListener("onTokenReceived", handleTokenReceived);
      handles.forEach((h) => h.remove());
    };
  }, []);

  // Envoie le token push à chaque login (chaque nouveau token de session),
  // même si le device token n'a pas changé.
  useEffect(() => {
    if (!token || !user?.id || !pendingToken) return;

    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/register/device`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ device_token: pendingToken }),
        });
        if (!res.ok) throw new Error(`API ${res.status}`);
        console.log("✅ Token FCM enregistré dans l'API");
      } catch (error) {
        console.error("❌ Erreur lors de l'enregistrement du token :", error);
      }
    })();
  }, [token, user?.id, pendingToken]);

  return null;
};

export default RegisterDevice;
